import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import { loginAdmin, clearError as clearAuthError } from "../redux/authSlice";
import { ROUTES } from "../utils/constants";
import ThemeToggle from "../Components/ThemeToggle";
import styles from "./Login.module.css";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const { loading, error } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const from = location.state?.from?.pathname;

  const validateForm = () => {
    if (!email.trim()) {
      setLocalError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setLocalError("Enter a valid email address");
      return false;
    }
    if (!password) {
      setLocalError("Password is required");
      return false;
    }
    if (password.length < 6) {
      setLocalError("Password must be at least 6 characters");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    dispatch(clearAuthError());

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await dispatch(loginAdmin({ email, password })).unwrap();
      const state = store.getState();
      const role = state.auth.role;
      const defaultRoute = role === "employee" ? ROUTES.EMPLOYEE_DASHBOARD : ROUTES.ADMIN_DASHBOARD;
      navigate(from || defaultRoute, { replace: true });
    } catch (err) {
      const message = err?.code === "auth/user-not-found"
        ? "No account found with this email. Please contact administrator."
        : err?.code === "auth/wrong-password"
        ? "Incorrect password."
        : err?.message || "Invalid credentials";
      setLocalError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginWrapper}>
        <div className={styles.brandSide}>
          <div className={styles.brandLogo}>
            <span className={styles.brandLogoIcon}>💊</span>
            Vyra Pharma
          </div>

          <div className={styles.brandContent}>
            <h2 className={styles.brandTitle}>Quality Medicines.<br />Trusted Healthcare.</h2>
            <p className={styles.brandText}>
              Sign in to access the Vyra Pharma portal. Manage employees, track operations, and stay connected with our healthcare network.
            </p>
          </div>

          <div className={styles.brandStats}>
            <div className={styles.statChip}>
              <strong>WHO-GMP</strong>
              <span>Certified</span>
            </div>
            <div className={styles.statChip}>
              <strong>19+</strong>
              <span>Products</span>
            </div>
            <div className={styles.statChip}>
              <strong>Pan-India</strong>
              <span>Delivery</span>
            </div>
          </div>
        </div>

        <div className={styles.formSide}>
          <div className={styles.formHeaderBar}>
            <button type="button" className={styles.backBtn} onClick={() => navigate("/")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Home
            </button>
            <ThemeToggle className={styles.loginThemeToggle} />
          </div>

          <div className={styles.formHeader}>
            <h1>Sign In</h1>
            <p>Enter your credentials to access your account</p>
          </div>

          {(error || localError) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              {error || localError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className={styles.inputGroup}>
              <label htmlFor="email">Email</label>
              <div className={styles.inputWrap}>
                <span className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 4L12 13 2 4" />
                  </svg>
                </span>
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">Password</label>
              <div className={styles.inputWrap}>
                <span className={styles.inputIcon}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.togglePassword}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="m14.12 14.12a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={isSubmitting || loading}>
              {isSubmitting || loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className={styles.loginFooter}>
            <p>© 2026 Vyra Pharmaceuticals Pvt. Ltd. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
